import { useTranslation } from 'react-i18next';

import {
  AddCircleLineIcon,
  Button,
  CloseLineIcon,
  Col,
  Form,
  NumberInput,
  Row,
  SelectInput,
  Text,
  TextAreaInput,
} from 'ui';
import type { Task } from 'models/activity';

type P = {
  isBookmark: boolean;
  isMultipleSubmissionsAllowed: boolean;
  name: string;
  tasks: Task[];
};

const ActivityTasksFormInputs = ({ isBookmark, isMultipleSubmissionsAllowed, name, tasks }: P) => {
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  // @ts-expect-error
  const renderFormItem = (task: Task, field: { key: number; name: number }) => {
    switch (task.input) {
      case 'number':
        return (
          <NumberInput
            input={{
              disabled: isBookmark,
              min: 0,
              placeholder: '123.4',
              step: 0.01,
            }}
            item={{
              ...field,
              label: task.name,
              name: [field.name, `${task.id}`],
              rules: [
                {
                  required: true,
                },
              ],
            }}
            key={task.id}
          />
        );
      case 'select':
        return (
          <SelectInput
            input={{
              disabled: isBookmark,
              getOptionLabel: (option) => option.name,
              getOptionValue: (option) => option.id,
              options: task.options ?? [],
              placeholder: t('Please make your selection'),
              showSearch: true,
            }}
            item={{
              ...field,
              label: task.name,
              name: [field.name, `${task.id}`],
              rules: [
                {
                  required: true,
                },
              ],
            }}
            key={task.id}
          />
        );
      case 'text':
        return (
          <TextAreaInput
            input={{
              disabled: isBookmark,
              placeholder: t('Description'),
            }}
            item={{
              ...field,
              label: task.name,
              name: [field.name, `${task.id}`],
              rules: [
                {
                  required: true,
                  whitespace: true,
                },
              ],
            }}
            key={task.id}
          />
        );
      default:
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  const renderFields = (field: any, activityNumber: number, removable: boolean, remove: (name: number) => void) => (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, no-restricted-syntax -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
    <Form.Item key={field.key} required={false}>
      <Row className="tasks-form-inputs__header">
        <Col span={23}>
          <Text weight="bold">
            {t('Activity')} {isMultipleSubmissionsAllowed ? activityNumber : null}
          </Text>
        </Col>
        {removable ? (
          <Col span={1}>
            <CloseLineIcon
              data-test-id="btn_delete-entry"
              onClick={() => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                remove(field.name);
              }}
            />
          </Col>
        ) : null}
      </Row>
      <Row className="tasks-form-inputs__wrapper">
        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases */}
        <Col span={24}>{tasks.map((task) => renderFormItem(task, field))}</Col>
      </Row>
    </Form.Item>
  );

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => {
        const first = fields[0];
        const rest = fields.slice(1);

        return (
          <div>
            {renderFields(first, 1, fields.length > 1, remove)}
            {rest.map((field, index) => renderFields(field, index + 2, true, remove))}
            {isMultipleSubmissionsAllowed ? (
              <Col className="tasks-form-inputs__add-activity-wrapper">
                {/* eslint-disable-next-line no-restricted-syntax -- TODO: fix later */}
                <Form.Item>
                  <Button
                    data-test-id="btn_add-activity"
                    icon={<AddCircleLineIcon className="tasks-form-inputs__add-activity-icon" />}
                    onClick={() => {
                      add();
                    }}
                    size="small"
                    type="link-primary"
                  >
                    {t('Add another activity')}
                  </Button>
                </Form.Item>
              </Col>
            ) : null}
          </div>
        );
      }}
    </Form.List>
  );
};

export default ActivityTasksFormInputs;
